import React, { useState } from "react"
import { View, ActivityIndicator } from "react-native"
import { type RehabSowTabs } from "@/screens/rehab/RehabSowScreen"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { Vertical } from "@/components/standard/BFLayout"
import BFText, { H4, H6, Small } from "@/components/standard/BFText"
import EditButton from "@/components/buttons/EditButton"
import { BFButton } from "@/components/buttons/CommonButtons"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import * as RehabEvents from "@/analytics/events/rehab"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import { getPropertyTypeLabel } from "@/screens/rehab/utils/Rehab"
import { displayNumber } from "@/utils/NumberUtils"
import { useSubmitRehabBudget } from "@/queries/rehab"
import { useRouter } from "expo-router"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import { loanAppDashboardPath } from "@/screens/loans/utils/LoanUtils"
import { stripLineBreaks } from "@/utils/StringUtils"
import { cn } from "@/lib/utils"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

export const RehabSummaryTab = ({
  setSelectedTab,
}: {
  setSelectedTab: (tab: RehabSowTabs) => void
}) => {
  const isDesktop = useIsDesktop()
  const { loanApplication } = useLoanContext()
  const router = useRouter() // For navigation
  const { isAndroid } = useDeviceAttributes()

  // const isDesktop = useIsDesktop()
  const [loading, setLoading] = useState(false)

  const {
    rehabBudgetId,
    lineItemsWithUserData,
    isLoading: rehabLoading,
    latestPropertyData,
    error,
    isEditable,
  } = useRehabBudgetingFeature(loanApplication, isDesktop)

  const { mutate: submitRehabBudget } = useSubmitRehabBudget()

  // **Handle Submission of Rehab**
  const onSubmit = () => {
    if (!rehabBudgetId) return

    setLoading(true)

    submitRehabBudget(
      { rehabBudgetId },
      {
        onSuccess: () => {
          showSuccessToast({
            title: "Rehab Budget Submitted",
            message: "Your rehab budget has been successfully submitted.",
            autoHide: true,
          })

          RehabEvents.rehabSOWSubmitted({
            propertyAddress:
              loanApplication?.property?.fullAddress || "Unknown Property",
            rehabBudget: 0, // Awaiting data
            selectedCategories: [], // Awaiting data
            additionalInfo: "tbd",
          })

          // Navigate back to the loan dashboard
          router.replace(loanAppDashboardPath(loanApplication.id))
        },
        onError: (error) => {
          console.error("Rehab submission failed:", error)
          showErrorToast({
            title: "Submission Failed",
            message:
              "There was an error submitting your rehab budget. Please try again.",
            autoHide: true,
          })
        },
        onSettled: () => {
          setLoading(false)
        },
      }
    )
  }

  const onEdit = () => {
    setSelectedTab("details")
  }

  const onEditDetails = () => {
    setSelectedTab("budget")
  }

  const formatPropertyValue = (value: any) => {
    if (typeof value === "string") return value
    if (typeof value === "number") return value.toLocaleString()
    return "N/A"
  }

  if (rehabLoading) return <ActivityIndicator size="small" />
  if (error) return <BFText>Error loading rehab budget</BFText>

  return (
    <View className="flex-1 bg-off-white w-full">
      <Vertical className="gap-0 my-0">
        {/* Category Summary */}
        <View className="flex flex-row items-center justify-between my-2">
          <H4 className="text-darkest mt-1 mb-2">Category Summary</H4>
          <EditButton
            onPress={onEditDetails}
            buttonText={isEditable ? "Edit" : "View"}
          />
        </View>

        <View className="border border-light-grey rounded-md bg-grey p-4 gap-y-1">
          {lineItemsWithUserData.map(({ label, budget }) => (
            <View key={label} className="mx-1 flex flex-row justify-between">
              <Small className="text-dark-grey">{stripLineBreaks(label)}</Small>
              <H6 className="text-midnight-blue">${budget.toLocaleString()}</H6>
            </View>
          ))}
        </View>

        {/* Property Details Summary */}
        <View className="flex flex-row items-center justify-between mt-6 mb-2">
          <H4 className="text-midnight-blue">Details Summary</H4>
          <EditButton
            onPress={onEdit}
            buttonText={isEditable ? "Edit" : "View"}
          />
        </View>

        <View className="border border-light-grey rounded-md bg-grey p-4 gap-y-1">
          {[
            { label: "Sq Ft", value: latestPropertyData.squareFootage },
            { label: "# of Beds", value: latestPropertyData.bedroomCount },
            {
              label: "# of Baths",
              value: displayNumber({
                value: Number(latestPropertyData.bathroomCount) || 0,
                precision:
                  Number(latestPropertyData.bathroomCount) % 1 === 0 ? 0 : 1,
              }),
            },
            {
              label: "Property Type",
              value: getPropertyTypeLabel(latestPropertyData.propertyType),
            },
          ].map(({ label, value }) => (
            <View key={label} className="flex flex-row justify-between">
              <Small className="text-dark-grey">{label}</Small>
              <H6 className="text-midnight-blue">
                {formatPropertyValue(value)}
              </H6>
            </View>
          ))}
        </View>

        {/* Submit Button */}
        <View className={cn("mt-8", isAndroid ? "mb-8" : "")}>
          <BFButton
            title={isEditable ? "Submit Rehab Budget" : "Budget in Review"}
            testID={"submit-rehab-budget-button"}
            onPress={onSubmit}
            loading={loading}
            variant="primary"
            disabled={!isEditable}
            pressOnceOnly={true}
          />
        </View>
      </Vertical>
    </View>
  )
}
